import React from 'react';
import * as S from './billing-transparency-block.molecule.style';
import { formatCurrency } from '../../../common/utils/format-currency';
import { useTranslation } from 'react-i18next';
import { Checkout, TrialType } from '../../../common/types/checkout';

type Props = {
  checkout: Checkout;
};

export const BillingTransparencyBlock: React.FC<Props> = ({ checkout }) => {
  const { t } = useTranslation();
  if (!checkout?.recorrencia) return null;

  const formatShortDate = (date: Date, locale: string) =>
    new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
      date,
    );

  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const addMonths = (date: Date, months: number) => {
    const d = new Date(date);
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);

    const daysInTargetMonth = new Date(
      d.getFullYear(),
      d.getMonth() + 1,
      0,
    ).getDate();
    d.setDate(Math.min(day, daysInTargetMonth));
    return d;
  };

  const addYears = (date: Date, years: number) => {
    const d = new Date(date);
    const month = d.getMonth();
    const day = d.getDate();

    d.setDate(1);
    d.setFullYear(d.getFullYear() + years, month, 1);

    const dim = new Date(d.getFullYear(), month + 1, 0).getDate();
    d.setDate(Math.min(day, dim));
    return d;
  };

  const alignDayOfMonth = (base: Date, anchorDayOfMonth: number) => {
    const d = new Date(base);
    const y = d.getFullYear();
    const m = d.getMonth();
    const dim = new Date(y, m + 1, 0).getDate();
    d.setDate(Math.min(anchorDayOfMonth, dim));
    return d;
  };

  const periodUnit = checkout?.plano?.billingConfig?.billingPeriodUnit; // BillingPeriodUnit
  const anchorDay =
    checkout?.plano?.billingConfig?.billingAnchorDayOfMonth ?? null;

  const trialEnabled = !!checkout?.plano?.trialConfig?.isTrialEnabled;
  const trialDays = checkout?.plano?.trialConfig?.trialDays ?? null;
  const trialType = checkout?.plano?.trialConfig?.trialType ?? null;

  const hasFreeTrial =
    trialEnabled &&
    typeof trialDays === 'number' &&
    trialDays > 0 &&
    trialType === TrialType.WithPayment;

  const hasPaidTrial =
    trialEnabled &&
    typeof trialDays === 'number' &&
    trialDays > 0 &&
    trialType === TrialType.Paid;

  const firstChargeAt = hasFreeTrial
    ? addDays(new Date(), trialDays!)
    : new Date();

  const resolveLocaleFromCheckout = () => {
    const country = (checkout?.pais || 'US').toUpperCase();
    const lang = (checkout?.linguagem || 'en').toLowerCase();

    const map: Record<string, string> = {
      US: 'en-US',
      BR: 'pt-BR',
      CL: 'es-CL',
      PY: 'es-PY',
      PE: 'es-PE',
      CO: 'es-CO',
      MX: 'es-MX',
      CA: 'en-CA',
      JP: 'ja-JP',
      GB: 'en-GB',
      DE: 'de-DE',
      FR: 'fr-FR',
      ES: 'es-ES',
      IT: 'it-IT',
      NL: 'nl-NL',
      PL: 'pl-PL',
      BE: 'nl-BE',
      SE: 'sv-SE',
      DK: 'da-DK',
      NO: 'nb-NO',
      FI: 'fi-FI',
      AT: 'de-AT',
      IE: 'en-IE',
      PT: 'pt-PT',
    };

    return map[country] ?? `${lang}-${country}`;
  };

  const computeNextOccurrenceAt = () => {
    if (!periodUnit) return null;

    if (anchorDay) {
      return alignDayOfMonth(addMonths(firstChargeAt, 1), anchorDay);
    }

    switch (periodUnit) {
      case 1: // Day
        return addDays(firstChargeAt, 1);
      case 2: // Week
        return addDays(firstChargeAt, 7);
      case 3: // Month
        return addMonths(firstChargeAt, 1);
      case 4: // Year
        return addYears(firstChargeAt, 1);
      default:
        return null;
    }
  };

  const locale = resolveLocaleFromCheckout();
  const nextChargeAt = computeNextOccurrenceAt();
  const nextChargeLabel = nextChargeAt
    ? formatShortDate(nextChargeAt, locale)
    : null;

  console.log('nextChargeAt', nextChargeAt);

  const amount = checkout?.valor;

  if (typeof amount !== 'number') return null;

  const price = formatCurrency(amount, checkout?.moeda);
  const priceToday = hasPaidTrial
    ? formatCurrency(
        checkout?.plano?.trialConfig?.trialPrice ?? 0,
        checkout?.moeda,
      )
    : price;

  const getIntervalLabel = () => {
    if (!periodUnit) return t('perMonth');

    switch (periodUnit) {
      case 1:
        return t('perDay');
      case 2:
        return t('perWeek');
      case 3:
        return t('perMonth');
      case 4:
        return t('perYear');
      default:
        return t('perMonth');
    }
  };

  const interval = getIntervalLabel();
  if (hasFreeTrial) {
    return (
      <S.BillingInfo role="note">
        <S.BillingLine>{t('trial', { days: trialDays })}</S.BillingLine>
        {nextChargeLabel ? (
          <S.BillingLine>
            {t('thenIntervalOn', { price, interval, date: nextChargeLabel })}
          </S.BillingLine>
        ) : (
          <S.BillingLine>
            {t('thenInterval', { price, interval })}
          </S.BillingLine>
        )}
      </S.BillingInfo>
    );
  }

  return (
    <S.BillingInfo role="note">
      <S.BillingLine>{t('chargedToday', { price: priceToday })}</S.BillingLine>
      {nextChargeLabel ? (
        <S.BillingLine>
          {t('thenIntervalOn', { price, interval, date: nextChargeLabel })}
        </S.BillingLine>
      ) : (
        <S.BillingLine>{t('thenInterval', { price, interval })}</S.BillingLine>
      )}
    </S.BillingInfo>
  );
};
